import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Bullets, Stages, Table, ValuesExercise, ComponentsMap, Approaches, Phases, ToolLink } from "@/components/app/learn/LearnSections";
import BehaviourPlan from "@/components/app/learn/BehaviourPlan";

export default function LearnArticle() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [a, setA] = useState(null);
  const [all, setAll] = useState([]);
  useEffect(() => { setA(null); api.get(`/learn/${slug}`).then((r) => setA(r.data)); api.post(`/guide/learn-read/${slug}`).catch(() => {}); api.get("/learn").then((r) => setAll(r.data.articles)); window.scrollTo(0, 0); }, [slug]);
  if (!a) return <div className="text-muted-foreground text-sm">Loading…</div>;
  const changeId = params.get("change");
  const idx = all.findIndex((x) => x.slug === slug);
  const next = all[idx + 1];
  return (
    <article data-testid="learn-article" className="max-w-3xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/app/learn" className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground"><ArrowLeft size={14} /> Learn</Link>
        {changeId && <Link to={`/app/changes/${changeId}`} data-testid="learn-back-to-change" className="inline-flex items-center gap-1 text-xs font-bold text-accent-700 hover:text-accent-900">Back to the change <ArrowRight size={14} /></Link>}
      </div>
      <div className="pp-overline mt-5">{a.group} · {a.minutes} min</div>
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-foreground" data-testid="learn-title">{a.title}</h1>
      <p className="text-lg text-subtle mt-3 leading-relaxed" data-testid="learn-opener">{a.opener}</p>
      <div className="mt-8 space-y-8">
        {a.sections.map((s, i) => <Section key={i} s={s} data={a.data} changeId={changeId} />)}
      </div>
      {next && <Link to={`/app/learn/${next.slug}${changeId ? `?change=${changeId}` : ""}`} data-testid="learn-next" className="mt-12 pp-card pp-card-hover p-5 flex items-center justify-between gap-3"><div><div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Next</div><div className="font-head font-extrabold mt-0.5">{next.title}</div></div><ArrowRight size={16} /></Link>}
    </article>
  );
}

function Section({ s, data, changeId }) {
  switch (s.type) {
    case "p": return <p className="text-body leading-relaxed">{s.text}</p>;
    case "bullets": return <Bullets s={s} />;
    case "stages": return <Stages s={s} stages={data.stages} />;
    case "table": return <Table s={s} />;
    case "values": return <ValuesExercise s={s} values={data.values} />;
    case "components": return <ComponentsMap components={data.components} tabs={data.tabs} changeId={changeId} />;
    case "approaches": return <Approaches types={data.types} order={data.priority_order} />;
    case "phases": return <Phases s={s} />;
    case "tool": return <ToolLink s={s} changeId={changeId} />;
    case "behaviour": return <BehaviourPlan changeId={changeId} questions={data.behaviour_questions} />;
    default: return null;
  }
}
